import { nip19 } from "nostr-tools"

export type DecodedNostrEntity =
  | { type: "npub"; pubkey: string }
  | { type: "nprofile"; pubkey: string; relays: string[] }
  | { type: "note"; id: string }
  | { type: "nevent"; id: string; author?: string; relays: string[] }
  | { type: "naddr"; kind: number; pubkey: string; identifier: string; relays: string[] }

/** Decodes a bech32 entity, returning undefined for anything unsupported. */
export function decodeNostrEntity(value: string): DecodedNostrEntity | undefined {
  const trimmed = value.trim()
  if (!trimmed.startsWith("nostr:")) {
    try {
      const decoded = nip19.decode(trimmed)
      return mapDecoded(decoded)
    } catch {
      return undefined
    }
  }
  try {
    const decoded = nip19.decode(trimmed.slice(6))
    return mapDecoded(decoded)
  } catch {
    return undefined
  }
}

function mapDecoded(decoded: ReturnType<typeof nip19.decode>): DecodedNostrEntity | undefined {
  switch (decoded.type) {
    case "npub":
      return { type: "npub", pubkey: decoded.data }
    case "nprofile":
      return { type: "nprofile", pubkey: decoded.data.pubkey, relays: decoded.data.relays ?? [] }
    case "note":
      return { type: "note", id: decoded.data }
    case "nevent":
      return {
        type: "nevent",
        id: decoded.data.id,
        author: decoded.data.author,
        relays: decoded.data.relays ?? [],
      }
    case "naddr":
      return {
        type: "naddr",
        kind: decoded.data.kind,
        pubkey: decoded.data.pubkey,
        identifier: decoded.data.identifier,
        relays: decoded.data.relays ?? [],
      }
    default:
      return undefined
  }
}

export function npubOf(pubkey: string): string {
  try {
    return nip19.npubEncode(pubkey)
  } catch {
    return pubkey
  }
}

export function noteIdOf(id: string): string {
  try {
    return nip19.noteEncode(id)
  } catch {
    return id
  }
}

export function neventOf(id: string, author?: string, relays?: string[]): string {
  try {
    return nip19.neventEncode({ id, author, relays })
  } catch {
    return id
  }
}

/** Short `npub1abcd…wxyz` label for pubkeys. */
export function shortNpub(pubkey: string): string {
  const npub = npubOf(pubkey)
  return `${npub.slice(0, 10)}…${npub.slice(-4)}`
}

/** Accepts a hex key, an npub or an nprofile and returns the hex pubkey. */
export function toPubkey(value: string): string | undefined {
  const trimmed = value.trim()
  if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed.toLowerCase()
  const decoded = decodeNostrEntity(trimmed)
  if (!decoded) return undefined
  if (decoded.type === "npub" || decoded.type === "nprofile") return decoded.pubkey
  return undefined
}

/** Accepts a hex id, note1… or nevent1… and returns the event id. */
export function toEventId(value: string): string | undefined {
  const trimmed = value.trim()
  if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed.toLowerCase()
  const decoded = decodeNostrEntity(trimmed)
  if (!decoded) return undefined
  if (decoded.type === "note") return decoded.id
  if (decoded.type === "nevent") return decoded.id
  return undefined
}
